import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Spinner, Dropdown, Button, Row, Col, Modal } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Input, Select, Empty } from "antd";

import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import useUsers from "hooks/userManagement/useUsers";
import IconButton from "components/common/IconButton";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import Guard from "components/access-control/rbac/Guard";
import useDebounce from "hooks/route-rules/useDebounce";

const STATUS_COLORS = {
  ACTIVE: "success",
  PENDING: "warning",
  DISABLED: "secondary",
  SUSPENDED: "warning",
  DELETED: "danger",
  UNKNOWN: "secondary",
};

// Real colors for stats section (Bootstrap matching tones)
const STATUS_STYLES = {
  ACTIVE: { border: "#198754", text: "#198754", bg: "#e6f4ea" },      // green
  PENDING: { border: "#ffc107", text: "#b08900", bg: "#fff8e1" },     // yellow
  DISABLED: { border: "#6c757d", text: "#6c757d", bg: "#f1f3f5" },    // gray
  SUSPENDED: { border: "#fd7e14", text: "#fd7e14", bg: "#fff4e6" },   // orange
  DELETED: { border: "#dc3545", text: "#dc3545", bg: "#fdeaea" },     // red
  UNKNOWN: { border: "#6c757d", text: "#6c757d", bg: "#f1f3f5" },
};

const STATUS_FILTER_OPTIONS = [
  { label: "🌍 All Statuses", value: "ALL" },
  { label: "🟢 Active", value: "ACTIVE" },
  { label: "🟡 Pending", value: "PENDING" },
  { label: "⚪ Disabled", value: "DISABLED" },
  { label: "🟠 Suspended", value: "SUSPENDED" },
  { label: "🔴 Deleted", value: "DELETED" },
  { label: "❔ Unknown", value: "UNKNOWN" },
];


// Compute stats including all statuses
const computeStats = (users) => {
  const allStatuses = STATUS_FILTER_OPTIONS.map((s) => s.value).filter((v) => v !== "ALL");
  const counts = allStatuses.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
  if (!users) return counts;
  users.forEach((u) => {
    const status = u.status || "UNKNOWN";
    counts[status] = (counts[status] || 0) + 1;
  });
  return counts;
};

const Users = ({ staff = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const usersListPath = `${location.pathname}${location.search}`;

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSearchActive = debouncedSearchTerm.trim().length >= 3;

  const [firstLoad, setFirstLoad] = useState(true);

  const { users, allUsersForStats, loading, error, totalPages, totalElements, refetch, searchUserProfiles, getUserById, deleteUserProfileProperty } =
    useUsers(page, pageSize, staff);

  // Reset page on search or status change
  useEffect(() => setPage(0), [debouncedSearchTerm, selectedStatus, staff]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isSearchActive) {
          await searchUserProfiles(debouncedSearchTerm, selectedStatus, page);
        } else {
          await refetch();
        }
      } finally {
        setFirstLoad(false);
      }
    };
    fetchData();
  }, [debouncedSearchTerm, page, isSearchActive, searchUserProfiles, selectedStatus, refetch]);

  // Refetch on window focus
  useEffect(() => {
    const handleFocus = () => refetch();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refetch]);

  // Keep the latest table URL so user details can always navigate back correctly.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem("olitaskUsersListPath", usersListPath);
  }, [usersListPath]);

  // Action handlers
  const handleViewUser = useCallback(
    (id) =>
      navigate(`/olitask/users/${id}`, {
        state: { fromPath: usersListPath },
      }),
    [navigate, usersListPath],
  );
  const handleEditUser = useCallback(async (id) => { await getUserById(id); navigate(`/olitask/users/${id}/edit`); }, [getUserById, navigate]);
  const confirmDelete = (id) => { setUserToDelete(id); setShowDeleteModal(true); setDeleteError(null); };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true); setDeleteError(null);
    try {
      await deleteUserProfileProperty(userToDelete);
      toast.success("User profile deleted successfully.");
      await refetch();
    } catch (err) {
      const apiMessage = err?.response?.data?.message || err?.message || "Failed to delete user profile.";
      setDeleteError(apiMessage);
      toast.error(apiMessage);
    } finally { setShowDeleteModal(false); setUserToDelete(null); setDeleting(false); }
  };

  const handleCancelDelete = () => { setShowDeleteModal(false); setUserToDelete(null); setDeleteError(null); };


  const stats = useMemo(() => computeStats(allUsersForStats), [allUsersForStats]);

  // Table columns
  const columns = useMemo(() => [
    {
      accessorKey: "id",
      header: "User ID",
      cell: ({ getValue }) => {
        const value = getValue() || "None";

        return (
          <div
            title={value} // full ID on hover
            style={{
              maxWidth: 90,        // reduce width
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 12,        // slightly smaller text
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      accessorFn: (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim(), id: "fullName", header: "Name",
      cell: ({ getValue }) => <div title={getValue()} style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getValue()}</div>
    },
    { accessorKey: "email", header: "Email", cell: ({ getValue }) => getValue() || "None" },
    { accessorKey: "gender", header: "Gender", cell: ({ getValue }) => getValue() || "None" },
   {
  accessorKey: "status",
  header: "Status",
  cell: ({ getValue }) => {
    const status = getValue() || "UNKNOWN";
    
    // We keep using your original STATUS_COLORS map
    const colorTheme = STATUS_COLORS[status] || "secondary";

    return (
      <div className="text-center">
        <SubtleBadge 
          bg={colorTheme} 
          className="fw-bold" // Matches your 600 weight requirement
          style={{ 
            borderRadius: "4px",    // Forces our "pill" design
            padding: "2px 6px",     // Forces compact size
            fontSize: "0.7rem",
            textTransform: "uppercase" 
          }}
        >
          {status.replaceAll("_", " ")}
        </SubtleBadge>
      </div>
    );
  },
  meta: {
    headerProps: { 
      className: "text-900 text-center fw-bold" // Standardized Bold Header
    },
    cellProps: { className: "text-center" },
  },
},
    {
      id: "actions", header: "Actions", cell: ({ row }) => {
        const user = row.original;
        return (
          <Dropdown className="action-dropdown p-0">
            <Dropdown.Toggle as="button" className="bg-transparent border-0 p-0 shadow-none text-secondary">⋯</Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleViewUser(user.id)}>View Details</Dropdown.Item>
              <Guard privilege="UPDATE_USERS"><Dropdown.Item onClick={() => handleEditUser(user.id)}>Edit</Dropdown.Item></Guard>
              <Guard privilege="DELETE_USERS"><Dropdown.Divider /><Dropdown.Item className="text-danger" onClick={() => confirmDelete(user.id)}>Delete</Dropdown.Item></Guard>
            </Dropdown.Menu>
          </Dropdown>
        );
      }
    }
  ], [handleViewUser, handleEditUser]);

  const tableData = useMemo(() => {
    if (!users) return [];
    if (selectedStatus === "ALL") return users;
    return users.filter((u) => (u?.status || "UNKNOWN") === selectedStatus);
  }, [users, selectedStatus]);

  const table = useAdvanceTable({ data: tableData, columns, sortable: true, pagination: true, perPage: pageSize });

  return (
    <>
      <WidgetSectionTitle
        className="mb-3 mt-3"
        title={staff ? "Staff Profiles" : "Customer Profiles"}
        icon="users"
        subtitle={`Manage ${staff ? "staff" : "customer"} user profiles.`}
        transform="shrink-2"
      />

    {/* ================= STATS + ADD BUTTON ================= */}
<div
  className="position-relative p-3 rounded-3 mb-4 d-flex align-items-center justify-content-between flex-wrap"
  style={{
    background: "#f7f5f5",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  }}
>
  {/* Stats boxes */}
  <div className="d-flex flex-nowrap gap-2 overflow-auto">
    {Object.keys(stats).map((status) => {
      const style = STATUS_STYLES[status] || STATUS_STYLES.UNKNOWN;

      return (
        <div
          key={status}
          className="p-2 rounded-3 d-flex flex-column align-items-center justify-content-center"
          style={{
            minWidth: 70,           // smaller min width
            maxWidth: 90,           // max width stays constrained
            flex: "0 1 auto",       // allows shrinking if too many boxes
            backgroundColor: "#fff",
            borderLeft: `4px solid ${style.border}`,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: style.text,
              marginBottom: 4,
              textAlign: "center",
            }}
          >
            {status}
          </div>

          <div
            style={{
              fontSize: 16,          // slightly smaller number
              fontWeight: 700,
              color: style.text,
            }}
          >
            {stats[status]}
          </div>
        </div>
      );
    })}
  </div>

  {/* Add button on right side with color */}
  <div className="ms-auto mt-2 mt-sm-0">
    <IconButton
      size="sm"
      icon="plus"
      transform="shrink-3"
      onClick={() => navigate(`/olitask/users/add?staff=${staff}`)}
      style={{
        backgroundColor: "#198754",
        color: "#fff",
        border: "1px solid #198754",
        fontWeight: 600,
      }}
    >
      <span className="ms-1">Add {staff ? "Staff" : "Customer"}</span>
    </IconButton>
  </div>
</div>

      {/* ================= SEARCH & FILTER ================= */}
{/* ================= SEARCH + STATUS FILTER ================= */}
<Row className="mb-2 g-3 align-items-center">
  <Col md={8} lg={9}>
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <Input
        prefix={<span style={{ fontSize: 14 }}>🔍</span>}
        placeholder="Search by name, email or ID..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        allowClear
        size="large"
        style={{
          borderRadius: 12,
          height: 30,
          flex: "1 1 300px",
          maxWidth: 300
        }}
      />

      <Select
        size="large"
        value={selectedStatus}
        onChange={(value) => {
          setSelectedStatus(value);
          setPage(0);
        }}
        options={STATUS_FILTER_OPTIONS}
        style={{
          minWidth: 180,
          height: 28
        }}
      />
    </div>
  </Col>

  <Col md={4} lg={3} className="text-end">
    <div style={{ fontSize: 13, color: "#64748b" }}>
      Results:{" "}
      <span className="fw-semibold text-dark">
        {tableData.length}
      </span>{" "}
      of{" "}
      <span className="fw-semibold text-dark">
        {totalElements}
      </span>
    </div>
  </Col>
</Row>

      {/* ================= RESULTS & TABLE ================= */}
      {/* <div className="d-flex justify-content-end mb-0.5" style={{ fontSize: 13, color: "#64748b" }}>
        Results: <span className="fw-semibold text-dark">{tableData.length}</span> of <span className="fw-semibold text-dark">{totalElements}</span>
      </div> */}

      {firstLoad || loading ? (
        <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Spinner animation="border" variant="success" />
        </div>
      ) : tableData.length === 0 ? (
        <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Empty description={`No ${staff ? "staff" : "customer"} profiles found`} />
        </div>
      ) : (
        <div style={{ width: "100%", overflowX: "hidden" }}>
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              headerClassName="bg-light text-nowrap align-middle text-dark"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{ bordered: true, striped: true, className: "fs-10 mb-0" }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={() => setPage((p) => Math.max(p - 1, 0))}
                onNext={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                pageSizeOptions={[10, 20, 30, 50, 100, 200, 500]}
              />
            </div>
          </AdvanceTableProvider>
        </div>
      )}

      {error && <div className="alert alert-danger mt-2">{error}</div>}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this user profile? This action cannot be undone.</p>
          {deleteError && <div className="alert alert-warning mt-3">{deleteError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Deleting...</> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Users;
